import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '@/state/store';
import { setCurrentlyViewingReward, setStartShare } from '@/state/features/app';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { openExternalLinkDiscord, triggerDiscordShare } from '@/components/DiscordAuthFlow/useDiscord';
import { pathToURL } from '@/utils/urls';
import { copyToClipboard } from '@/utils/dom';
import { sendEventV2 } from '@/utils/analytics';
import Modal from '@/components/Modal';
import Button from '@/components/Forms/Button';
import Icon from '@/components/Icon';
import { Reward } from '@/models/misc';

import * as styles from './styles.module.scss';

export const RedeemRewardModal = () => {
    const dispatch = useAppDispatch();
    const currentlyViewingReward = useAppSelector((state) => state.app.currentlyViewingReward);
    const [isVisible, setIsVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const rewardRef = useRef<Reward | null>(null);

    if (currentlyViewingReward) {
        rewardRef.current = currentlyViewingReward;
    }

    const reward = rewardRef.current;

    useEffect(() => {
        if (currentlyViewingReward) {
            setCopied(false);
            sendEventV2('reward__modal_opened', [currentlyViewingReward.name]);
            requestAnimationFrame(() => setIsVisible(true));
        }
    }, [currentlyViewingReward?.id]);

    if (!reward) return;

    const closeModal = () => {
        sendEventV2('reward__modal_closed', [reward.name]);
        setIsVisible(false);
        dispatch(setCurrentlyViewingReward(null));
    };

    const copyCode = async (code: string) => {
        const success = await copyToClipboard(code);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            sendEventV2('reward__modal_code_copied', [reward.name]);
        }
    };

    const handleCta = async () => {
        if (reward.ctaMethod === 'open_link') {
            sendEventV2('reward__modal_cta_clicked', [reward.name, reward.ctaMethod, reward.externalUrl]);
            if (IS_DISCORD_ACTIVITY) {
                openExternalLinkDiscord(reward.externalUrl);
            } else {
                window.open(reward.externalUrl, '_blank', 'noopener,noreferrer');
            }
        } else if (reward.ctaMethod === 'send_dm') {
            sendEventV2('reward__modal_cta_clicked', [reward.name, reward.ctaMethod, reward.messageText]);
            if (IS_DISCORD_ACTIVITY) {
                await triggerDiscordShare(reward.messageText);
            } else {
                dispatch(setStartShare({ message: reward.messageText }));
            }
        }
    };

    const renderReward = () => {
        if (reward.redemptionType === 'code' && reward.redemptionValue) {
            return (
                <div className={styles.codeRow}>
                    <span
                        className={classNames(styles.codeValue, {
                            [styles.longCode]: reward.redemptionValue?.length > 12,
                        })}
                    >
                        {reward.redemptionValue}
                    </span>
                    <button className={styles.copyButton} onClick={() => copyCode(reward.redemptionValue)}>
                        {copied ? 'Copied!' : 'Copy'}
                        <Icon type="copy" size={16} />
                    </button>
                </div>
            );
        }

        if (reward.redemptionType === 'qr_code' && reward.redemptionImagePath) {
            return (
                <div className={styles.qrContainer}>
                    <img className={styles.qrImage} src={pathToURL(reward.redemptionImagePath)} alt="QR Code" />
                </div>
            );
        }

        if (reward.redemptionType === 'barcode' && reward.redemptionImagePath) {
            return (
                <div className={styles.barcodeContainer}>
                    <img className={styles.barcodeImage} src={pathToURL(reward.redemptionImagePath)} alt="Barcode" />
                </div>
            );
        }

        return null;
    };

    return (
        <div className={styles.RedeemRewardModal}>
            <Modal
                isVisible={isVisible}
                onClose={closeModal}
                hasCloseButton
                canScroll
                shouldCloseOnExternalClick={false}
                leaveSpaceAboveAndBelow={false}
                modalClassname={styles.modal}
                contentClassname={styles.content}
                zIndex={150}
            >
                <>
                    <h2 className={styles.modalTitle}>{reward.modalTitle}</h2>
                    {reward.mainImagePath && (
                        <div className={styles.rewardImageContainer}>
                            <img
                                className={styles.rewardImage}
                                src={pathToURL(reward.mainImagePath)}
                                alt={reward.name}
                            />
                        </div>
                    )}
                    <div className={styles.textContent}>
                        <h3 className={styles.title}>{reward.name}</h3>
                        {reward.description && <p className={styles.description}>{reward.description}</p>}
                        {reward.redemptionInstructions && (
                            <p className={styles.description}>{reward.redemptionInstructions}</p>
                        )}
                        {reward.readMoreUrl && (
                            <a
                                className={styles.readMoreLink}
                                href={reward.readMoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    sendEventV2('reward__modal_read_more_clicked', [reward.name, reward.readMoreUrl]);
                                    if (IS_DISCORD_ACTIVITY) {
                                        openExternalLinkDiscord(reward.readMoreUrl);
                                    }
                                }}
                            >
                                {reward.readMoreText ?? reward.readMoreUrl}
                            </a>
                        )}
                    </div>
                    <div className={styles.rewardContainer}>{renderReward()}</div>
                    {reward.ctaText && (
                        <div className={styles.ctaContainer}>
                            <Button className={styles.ctaButton} onClick={handleCta}>
                                {reward.ctaText}
                            </Button>
                        </div>
                    )}
                </>
            </Modal>
        </div>
    );
};

export default RedeemRewardModal;
/* Example
{
    "id": "c0ffee00-1a2b-4c3d-9e8f-001122334455",
    "createdAt": "2026-05-18T08:16:50.693Z",
    "universeId": "97cab966-aa05-4e90-8b0b-f1448c318de5",
    "modalTitle": "Congrats!",
    "name": "Example Reward",
    "description": "A short description of the reward.",
    "mainImagePath": "rewards/example-main.png",
    "secondaryImagePath": "rewards/example-thumb.png",
    "redemptionInstructions": "Show this screen at the till before paying.",
    "sfxUrl": "",
    "externalUrl": "https://example.com/redeem",
    "ctaText": "Claim reward",
    "ctaMethod": "open_link",
    "readMoreUrl": "https://example.com/terms",
    "readMoreText": "Read terms",
    "isFiniteSupply": true,
    "oneCodeForAll": false,
    "messageText": "Here is your reward code: ABC123",
    "redemptionType": "code",
    "redemptionValue": "ABC123",
    "redemptionImagePath": ""
}
*/
